import { useState,useRef, useEffect } from "react";
import { useDispatch,useSelector } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";
import _ from "lodash";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../../PageLayout";
import DesignHeader from "../DesignJobs/DesignHeader";
import FooterButtons from "../DesignJobs/FooterButtons";
import FullkitHeader from "./FullkitHeader";
import Tasks from "./Tasks";
import UnAuthorizedComponent from "../../UnAuthorized/UnAuthorizedComponent";
import { UnsavedChangesDialog, useUnsavedChangesWarning } from "../../common/NavigationPrompt";
import { getCustomFullkitDropdown } from "../../../store/actions/CustomFullkitActions";
import moment from "moment-timezone";
import { createCustomFullkit,submitCustomFullkit } from "../../../apis/customFullkitApi";
import { Toast } from "primereact/toast";
import { getTaskDetails } from "../../../store/actions/taskDetailAction";
import {
  sortedDataAsPerJobID,
  checkFormValidityCFK,
  redirectToProjectPlanOrTaskPage,
} from "../../../utils";
import "./index.scss"

const taskObj = {
  DesignJobName: "",
  Type: "",
  Value: "",
  End_Date: null,
  Assignee: "",
  Sequence: { seq: "1" },
  Design_Job_Status: "",
  IsFileUploadMandatory: false
};

function CustomFullKit() {
  const navigate = useNavigate();
  const toast = useRef();
  let { TaskID, page1, page2, pageType, ProjectID } = useParams();
  const User = useSelector((state) => state.UserReducer);
  const userInformation = User.userInformation;

  const [tasks, setTasks] = useState([]);
  const CFKDesignDisabled = (TaskID !== undefined && TaskID !== "") ? true : false;

  const [enableSubmit, setEnableSubmit] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [fullkitName, setFullkitName] = useState("");
  const [initialFullkitName, setInitialFullkitName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedTaskList, setSelectedTaskList] = useState([]);
  const [errorAlert, setErrorAlert] = useState(false);
  const [accessType, setAccessType] = useState("Write");
  const [accessSecurityMatrix, setUserAccess] = useState(true);
  const [checkReadWriteAccess, setCheckReadWriteAccess] = useState(true);
  const [pageEdited, setPageEdited] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [InitialTasks, setInitialTasks] = useState([]);
  const [sequenceListOptions, setSequenceListOptions] = useState([]);
  const [sequenceDisabledListOptions, setSequenceDisabledListOptions] = useState([]);
  const [taskStatus, setTaskStatus] = useState("");
  const { markDirty, markClean } = useUnsavedChangesWarning();

  const {
    userDetails: { AccessGroup, UserGroup },
  } = useSelector((state) => state?.UserDetailsReducer);

  const dispatch = useDispatch();
  const { customFullkitDropdown, loading } = useSelector((state) => state.CustomFullkitReducer);

  let { TaskDetailsData } = useSelector((state) => state.TaskDetailsReducer);

  TaskDetailsData = (TaskID !== undefined && TaskID !== "") ? TaskDetailsData : null;

  const id = `${TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Key}`;
  const headerName = TaskDetailsData?.ArtworkAgilityTasks?.[0]?.TaskPlaceholderName || "Custom Fullkit";
  const shortName = TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Type || "Custom Fullkit ";

  let checkTaskIsComplete =
    TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Status === "Complete" ||
    TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Status === "Cancelled";

  useEffect(() => {
    if (tasks.length === 0) setEnableSubmit(true);
    else {
      checkFormValidityCFK(tasks, fullkitName, selectedTaskList, setEnableSubmit);
    }
    const isObjectEqual = JSON.stringify(InitialTasks) === JSON.stringify(tasks);
    if (!isObjectEqual) {
      setPageEdited(true);
    } else {
      setPageEdited(false);
      setEnableSubmit(true);
    }

    checkMaxSequenceAndPopulateSequenceListOptions()
  }, [tasks, fullkitName, selectedTaskList]);

  // 3109 security matrix
  const userAccessPayLoad = {
    ArtworkAgilityPage: {
      UserId: userInformation?.userid,
      ProjectID: ProjectID,
      UserGroup: UserGroup,
    },
  };

  useEffect(() => {
    //load the api only when user has access as per security matrix
    if (UserGroup?.length && userInformation?.userid && checkReadWriteAccess) {
      dispatch(getCustomFullkitDropdown(ProjectID));
    }
  }, [dispatch, UserGroup?.length, userInformation?.userid, checkReadWriteAccess]);

  useEffect(() => {
    if( userInformation?.userid && UserGroup?.length && TaskID && ProjectID ){
      setLoader(false);
      dispatch(getTaskDetails(TaskID, ProjectID, userAccessPayLoad));
    }
  }, [dispatch, TaskID, ProjectID, userInformation?.userid, UserGroup?.length]);

  useEffect(() => {
    if (TaskDetailsData) {
      const allCFKJobs = TaskDetailsData?.ArtworkAgilityTasks?.[0]?.DesignJobDetails || [];
      const sortedData = sortedDataAsPerJobID(allCFKJobs, "Design_Job_ID").map((item) => ({
        ...item,
        Sequence: item?.TaskSequence ? { seq: Number(item?.TaskSequence) } : null,
      }));
      setInitialTasks(sortedData);
      setTasks(sortedData);

      setSequenceDisabledListOptions(filterDisabledIndices(sortedData));
      setFullkitName(TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Name);
      setInitialFullkitName(TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_Name);
      setTaskStatus(TaskDetailsData.ArtworkAgilityTasks[0].Task_Status);
      setSelectedTaskList(TaskDetailsData?.TaskList);
    } else {
      setTasks([]);
      addNewTask(true);
      setFullkitName(customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.Task_Name);
      setInitialFullkitName(customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.Task_Name);
      setProjectName(customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.Project_Name);
      setSelectedTaskList([]);
    }
  }, [TaskDetailsData, customFullkitDropdown]);

  useEffect(() => {
    if (pageEdited) {
      markDirty();
    } else {
      markClean();
    }
  }, [pageEdited]);

  useEffect(() => {
    markClean();
    setPageEdited(false);
  }, []);

  const checkMaxSequenceAndPopulateSequenceListOptions = () => {
    if (tasks.length === 1) return;

    // find the maximum sequence value selected
    const sequenceList = tasks.map((task) => task?.Sequence?.seq).sort((a, b) => a - b).filter((val) => val);
    const maxSeqVal = sequenceList.pop() ?? "0";      // if sequenceList is empty then consider maxSeqVal as 0 to show 1 as sequence option for newly added task

    const sequences = Array.from(
      { length: Number(maxSeqVal) + 1 > tasks.length ? Number(maxSeqVal) : Number(maxSeqVal) + 1 },         // restrict sequence value to maximum number of rows present on the UI
      (_, i) => i + 1,
    );

    setSequenceListOptions(
      sequences.map((i) => ({
        seq: i + "",
      })),
    );
  };

  const handleCancel = () => {
    redirectToProjectPlanOrTaskPage(page1, page2, pageType, ProjectID, navigate);
  };

  const handleCancelClick = () => {
    if (pageEdited) {
      setShowDialog(true);
    } else {
      handleCancel();
    }
  };

  // it will hide warning dialog
  const cancelNavigation = () => {
    setShowDialog(false);
  };
  // it will navigate to previously saved url navigate(pendingNavigation);
  // by doing setShowDialog(false) and setPendingNavigation("");
  const confirmNavigation = () => {
    markClean();
    handleCancel();
    setPageEdited(false);
    setShowDialog(false);
  };

  const handleProjectNameClick = () => {
    if (pageEdited) {
      setShowDialog(true);
    } else {
      handleProjectName();
    }
  };

  const handleProjectName = () => {
    if (page2 && page2.toLowerCase() === "projectplan") {
      return navigate(`/${page1}/${page2}/${ProjectID}`);
    } else if (pageType) {
      if (pageType.toLowerCase() === "mytasks") {
        navigate(`/myProjects/projectPlan/${ProjectID}`);
      } else if (pageType.toLowerCase() === "alltasks") {
        navigate(`/allProjects/projectPlan/${ProjectID}`);
      }
    }
  };

  const addNewTask = (isNew = false) => {
    if (isNew) {
      setSequenceListOptions([{ seq: "1" }]);
      setTasks([taskObj]);
      setInitialTasks([taskObj]);
    } else {
      const clonedTasks = _.cloneDeep(tasks);

      // creating enabled sequence list for completed tasks
      const sequenceNotDisabledListOptions = _.difference(
        sequenceListOptions?.map((sq) => sq?.seq),
        sequenceDisabledListOptions
      );

      clonedTasks.push({ ...taskObj, Sequence: { seq: sequenceNotDisabledListOptions[0] } });
      setTasks(clonedTasks);
    }
  };

  const formatPayloadDate = (date) => {
    if (date) {
      const formattedDate = moment(date, "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)").format(
        "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]"
      );
      return formattedDate;
    } else {
      return date;
    }
  };

  const getCFKCollectFormDataTasks = (data) => {
    return data.map((task) => {
      if ("Design_Job_ID" in task) {
        // for existing tasks during edit scenario
        return {
          instruction: "APPEND",
          target: "DefineCustomFullkitList",
          content: {
            AWMTaskID: task?.AWM_Task_ID,
            Project_TaskID: task?.Project_TaskID,
            DesignJobName: task?.Design_Job_Name,
            DesignJobID: task?.Design_Job_ID,
            Select: "true",
            Action: "update",
            Assignee: task?.Assignee,
            Type: task?.Type,
            Value: task?.Value,
            End_Date: task?.EndDate,
            IsFileUploadMandatory: task?.IsFileUploadMandatory,
            TaskSequence: task?.TaskSequence || "",
          },
        };
      } else {
        // for newly created tasks during edit & first time scenarios
        return {
          instruction: "APPEND",
          target: "DefineCustomFullkitList",
          content: {
            DesignJobName: `${task.Type}_FK_${fullkitName}_${task.Value}`,
            DesignJobID: "",
            Select: "true",
            Action: "add",
            Assignee: task?.Assignee?.Person,
            Type: task?.Type,
            Value: task?.Value,
            End_Date: formatPayloadDate(task?.End_Date),
            IsFileUploadMandatory: task?.IsFileUploadMandatory,
            TaskSequence: task?.Sequence?.seq ? Number(task?.Sequence?.seq) : "",
          },
        };
      }
    });
  };

  const getSelectedTaskList = () => {
    const selectedTaskListOptions = [];

    selectedTaskList.forEach((obj) => {
      let temp = {};
      temp.instruction = "APPEND";
      temp.target = "DependentTaskList";
      temp.content= {
        AWMTaskID : obj.AWMTaskID || ""
      } 
      selectedTaskListOptions.push(temp);
    });
    return selectedTaskListOptions;
  };

  const collectFormData = (newCFKTaskID, data = []) => {
    const CFKCollectFormDataTasks = getCFKCollectFormDataTasks(data)
    const CSKSelectedTaskList = getSelectedTaskList();

    const formData = {
      caseTypeID : "PG-AAS-Work-DefineCustomFullkit",
      content: {
        AWMTaskID: newCFKTaskID,
        AWMProjectID: ProjectID,
      },
      pageInstructions: [...CFKCollectFormDataTasks, ...CSKSelectedTaskList],
    };
    return formData;
  }

  const handleDelete = (index) => {
    const updatedTasks = _.cloneDeep(tasks);

    sequenceListOptions.pop()

    // Remove the deleted item from the updatedTasks array
    updatedTasks.splice(index, 1);

    /* resetting sequence
     * case 1 (creating fullkit): reset sequence for all kits created
     * case 2 (editing fullkit): reset sequence only for newly added kits where Design_Job_ID is not present
     */
    const resetSequence = updatedTasks.map((item) =>
      "Design_Job_ID" in item ? item : { ...item, Sequence: "" }
    );
    setTasks(resetSequence);
  };

  // decrease the sequence value by 1 if a task is having sequence value greater than the existing task length known to backend
  const adjustTaskSequence = (sequence, maxExistingTaskLength) => {
    if (sequence) {
      const sq = Number(sequence);
      return sq === maxExistingTaskLength ? sq - 1 + "" : sequence;
    }
    return sequence;
  };

  const handleDeleteJob = async (Design_Job_ID) => {
    setLoader(true);

    const existingTasks = tasks.filter((task) => "Design_Job_ID" in task);      // filtering tasks which are already stored in backend
    const updatedDataList = existingTasks.map((task) => ({
      instruction: "APPEND",
      target: "DefineCustomFullkitList",
      content: {
        AWMTaskID: task?.AWM_Task_ID,
        Project_TaskID: task?.Project_TaskID,
        DesignJobName: task?.Design_Job_Name,
        DesignJobID: task?.Design_Job_ID,
        Select: "true",
        Action: Design_Job_ID === task?.Design_Job_ID ? "delete" : "update",
        Assignee: task?.Assignee,
        Type: task?.Type,
        Value: task?.Value,
        End_Date: task?.EndDate,
        IsFileUploadMandatory: task?.IsFileUploadMandatory,
        TaskSequence:
          Design_Job_ID === task?.Design_Job_ID
            ? task?.TaskSequence
            : adjustTaskSequence(task?.TaskSequence, existingTasks?.length), // keeping task sequence same for deleted task and updating rest
      },
    }));

    const formDataDelete = {
      caseTypeID: "PG-AAS-Work-DefineCustomFullkit",
      content: {
        AWMTaskID: TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_ID,
        AWMProjectID: TaskDetailsData?.ArtworkAgilityPage?.AWM_Project_ID,
      },
      pageInstructions: updatedDataList,
    };

      const res_submitCustomFullkit = await submitCustomFullkit(formDataDelete, id, TaskDetailsData?.ArtworkAgilityPage?.Etag);
    dispatch(getTaskDetails(TaskID, ProjectID, userAccessPayLoad));
      if(res_submitCustomFullkit.status === 200){
      setLoader(false);
      toast.current.show({
          severity:'success', 
          summary: 'Success', 
          detail:'Custom Fullkit deleted successfully', 
          life: 3000
      });
      }else{
      setLoader(false);
      toast.current.show({
          severity:'warn', 
          summary: 'Error', 
          detail:'Error in deleting custom fullkit', 
          life: 3000
      });
    }
  };

  const onSubmit = async () => {
    setLoader(true);

    if (TaskDetailsData?.ArtworkAgilityPage?.Etag) {
      let FormData = collectFormData(TaskDetailsData?.ArtworkAgilityTasks?.[0]?.Task_ID, tasks);
      const res_submitCustomFullkit = await submitCustomFullkit(
        FormData,
        id,
        TaskDetailsData?.ArtworkAgilityPage?.Etag
      );
      if (res_submitCustomFullkit.status === 200) {
        setLoader(false);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Custom Fullkit updated successfully",
          life: 3000,
        });
        setTimeout(() => navigate(`/${page1}/${page2}/${ProjectID}`), 4000);
      } else {
        setLoader(false);
        toast.current.show({
          severity: "warn",
          summary: "Error",
          detail: "Error in updating custom fullkit",
          life: 3000,
        });
      }
    } else {
      const formData_CCFK = {
        AWMProjectID: ProjectID,
        Full_Name: fullkitName,
        Assignee: userInformation?.userid,
        ProjectName: customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.Project_Name || "",
        BU: customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.BU || "",
        Region: customFullkitDropdown?.ArtworkAgilityTasks?.[0]?.Region || "",
      };

      const response_CCFK = await createCustomFullkit(formData_CCFK, ProjectID);
      if (response_CCFK.status === 200) {
        const newCFKTaskID = response_CCFK?.data?.AWMTaskID || "";
        let FormData = collectFormData(newCFKTaskID, tasks);

        const res_submitCustomFullkit = await submitCustomFullkit(
          FormData,
          response_CCFK?.data?.TaskKey,
          response_CCFK?.data?.DateTime
        );
        if (res_submitCustomFullkit.status === 200) {
          setLoader(false);
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Custom Fullkit created successfully",
            life: 3000,
          });
          setTimeout(() => navigate(`/${page1}/${page2}/${ProjectID}`), 4000);
        } else {
          setLoader(false);
          toast.current.show({
            severity: "warn",
            summary: "Error",
            detail: "Error in creating custom fullkit",
            life: 3000,
          });
        }
      } else {
        setLoader(false);
        toast.current.show({
          severity: "warn",
          summary: "Error",
          detail: "Error in creating custom fullkit",
          life: 3000,
        });
      }
    }
  };

  // creating disabled sequence list
  const filterDisabledIndices = (data) => {
    const trackSeqStatus = {};
    const disabledIndexList = [];

    for (const i of data) {
      if (i?.TaskSequence in trackSeqStatus) {
        trackSeqStatus[i?.TaskSequence] = trackSeqStatus[i?.TaskSequence]
          ? i?.Status === "Complete"
          : false;
      } else {
        trackSeqStatus[i?.TaskSequence] = i?.Status === "Complete";
      }
    }

    for (const seq in trackSeqStatus) {
      if (trackSeqStatus[seq]) {
        disabledIndexList.push(seq);
      }
    }

    return disabledIndexList;
  };

  return (
    <PageLayout>
      <Toast ref={toast} />
      {accessSecurityMatrix ? (
        <LoadingOverlay active={loader || loading} spinner text="">
          <DesignHeader
            setAddNewDesign={() => addNewTask(tasks?.length === 0)}
            headerName={headerName}
            label={shortName}
            lastLabel={"Custom Fullkit"}
            checked={checked}
            pageEdited={pageEdited}
            setPageEdited={setPageEdited}
            setChecked={setChecked}
            checkReadWriteAccess={checkReadWriteAccess}
            showCommonCheckbox={"No"}
            checkTaskISComplete={checkTaskIsComplete}
          />
          <FullkitHeader
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            checkReadWriteAccess={checkReadWriteAccess}
            fullkitName={fullkitName}
            initialFullkitName={initialFullkitName}
            projectName={projectName}
            setFullkitName={setFullkitName}
            setInitialFullkitName={setInitialFullkitName}
            selectedTaskList={selectedTaskList}
            setSelectedTaskList={setSelectedTaskList}
            errorAlert={errorAlert}
            setErrorAlert={setErrorAlert}
            taskListOptions={customFullkitDropdown?.TaskList}
            CFKDesignDisabled={CFKDesignDisabled}
            handleProjectNameClick={handleProjectNameClick}
          />
          <div className="fullkit-parentJob-details">
            {tasks &&
              tasks.length > 0 &&
              tasks.map((taskDetails, index) => (
                <Tasks
                  key={`taskno-${index}`}
                  index={index}
                  taskDetails={taskDetails}
                  taskStatus={taskStatus}
                  checkReadWriteAccess={checkReadWriteAccess}
                  fullkitName={fullkitName}
                  tasks={tasks}
                  setTasks={setTasks}
                  assigneeListOptions={customFullkitDropdown?.OwnersList}
                  sequenceListOptions={sequenceListOptions}
                  sequenceDisabledListOptions={sequenceDisabledListOptions}
                  setPageEdited={setPageEdited}
                  handleDelete={handleDelete}
                  handleDeleteJob={handleDeleteJob}
                  Design_Job_ID={taskDetails?.Design_Job_ID}
                  Design_Job_Status={taskDetails?.Design_Job_Status}
                  CFKDesignDisabled={CFKDesignDisabled}
                />
              ))}
          </div>
          <FooterButtons
            accessType={accessType}
            handleCancel={handleCancelClick}
            onSubmit={onSubmit}
            formValid={enableSubmit}
            bottomFixed={true}
            hideSaveButton={true}
          />
        </LoadingOverlay>
      ) : (
        AccessGroup && AccessGroup.length && <UnAuthorizedComponent nowrapper={true} />
      )}
      {/* UnsavedChangesDialog popup*/}
      <UnsavedChangesDialog
        visible={showDialog}
        onCancel={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </PageLayout>
  );
}

export default CustomFullKit;
